// BondLadderTab.jsx
import React, { useEffect, useCallback } from "react";
import { IntegerInput } from './NumericInputs'; // Updated import
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useBondLadderStore } from "../../Store/bondLadderStore";
import { inputStyle } from "./styles";
import { bondTypes, fieldOptions, bondLadderTypeOptions } from "./optionsHelper";
import BondCharacteristicField from "./BondCharacteristicsFields";
import FilterDetailsBoxes from "./FilterDetailsBoxes";
import { preprocessFilters } from "../OrderComponents/columnConfigUtils";
import { fetchFilterInitialValues, fetchIssuers, fetchSnpRatingOptions, fetchMoodyRatingOptions, getSecurityChargeOptions } from "../../http/api";
import dayjs from "dayjs";
import { useAuth } from "../../Context/AuthProvider";
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { jwtDecode } from 'jwt-decode';
import { BOND_TYPES, ORDER_SIDES, USER_ROLES, ORDER_ENTRY_SECONDARY_SOURCES } from "../../Constants/AppConstants";
import { tokens } from "../../Common/designTokens"; // added

const textAreaStyle = {
  width: '100%',
  minHeight: '32px',
  padding: '8px',
  boxSizing: 'border-box',
  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
  borderRadius: '8px', // consistent 8px radius
  backgroundColor: tokens.colors.white,
  resize: 'vertical',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  wordWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  overflowX: 'hidden',
  lineHeight: '32px',
  color: tokens.colors.fontColour,
};

const stickyBoxStyle = {
  position: "sticky",
  top: 0,
  backgroundColor: tokens.colors.bodyBackgroundColor,
  zIndex: 10,
};

const BondLadderTab = ({ accountIdentifiers: propAccountIdentifiers, readOnly = false, errors = {}, onValidationChange, secondarySource }) => {
  const {
    bondLadderData,
    setBondLadderData,
    clearFieldError,
    checked,
    setChecked,
    removeFilter,
    accountIdentifiers: storeAccountIdentifiers,
    setBondLadderErrors,
    validationErrors,
    clearValidationErrors,
    fetchAccounts,
  } = useBondLadderStore();

  // Add issuerOptions state
  const [issuerOptions, setIssuerOptions] = React.useState([]);
  // Add Moody/S&P options state
  const [moodyRatingOptions, setMoodyRatingOptions] = React.useState([]);
  const [spRatingOptions, setSpRatingOptions] = React.useState([]);
  const [securityChargeOptions, setSecurityChargeOptions] = React.useState([]);

  const [isCommentsFocused, setIsCommentsFocused] = React.useState(false);
  const { token: jwtToken } = useAuth();
  const decodedToken = jwtDecode(jwtToken);
  const tokenRole = decodedToken.role;

  // When opened from the staging blotter "Source" column, render the ladder's
  // characteristic filters as read-only chips instead of checkbox-wise fields.
  const viewAsChips =
    secondarySource === ORDER_ENTRY_SECONDARY_SOURCES.SOURCE_COLUMN_VIEW;
  const chipInput = React.useMemo(() => {
    if (!viewAsChips) return null;
    // type & side are already shown in the read-only header section above.
    const { type, side, ...rest } = preprocessFilters(bondLadderData) || {};
    return rest;
  }, [viewAsChips, bondLadderData]);

  const effectiveAccountIdentifiers = propAccountIdentifiers;

  useEffect(() => {
    if (tokenRole === USER_ROLES.TRADER) {
      fetchAccounts(jwtToken);
    }
  }, [tokenRole, jwtToken, fetchAccounts]);

  // Only set account once after mount if not set
  useEffect(() => {
    if (
      effectiveAccountIdentifiers &&
      effectiveAccountIdentifiers.length > 0 &&
      !bondLadderData.account
    ) {
      setBondLadderData({ ...bondLadderData, account: effectiveAccountIdentifiers[0] });
    }
  }, [effectiveAccountIdentifiers, bondLadderData.account]);

  const handleCheckboxChange = (panel, field) => (event) => {
    const isChecked = event.target.checked;
    setChecked(panel, field, isChecked);
    if (!isChecked) {
      removeFilter(field);
    } else if (field === 'securityCharge') {
      const current = bondLadderData.securityCharge;
      if (!current || (Array.isArray(current) && current.length === 0)) {
        setBondLadderData({ securityCharge: ['ALL'] });
      }
    }
  };
  useEffect(() => {
    const tabEnum = { 0: "Corp", 1: "Gov", 2: "Muni" }; // Changed order: Corp, Gov, Muni
    const tabValue = bondLadderData.bondType === BOND_TYPES.CORPORATES ? 0 :
                     bondLadderData.bondType === BOND_TYPES.TREASURY ? 1 : 2;
    const tabKey = tabEnum[tabValue];
    const payload = { type: tabKey };
  
    const getFilterInitialValues = async (jwtToken, payload) => {
      const res = await fetchFilterInitialValues(jwtToken, payload);
      if (res) {

        const prefilledData = {
          couponMin: res.minCoupon ?? null,
          couponMax: res.maxCoupon ?? null,
          priceMin: res.minPrice ?? null,
          priceMax: res.maxPrice ?? null,
          yieldMin: res.minYtm ?? null,
          yieldMax: res.maxYtm ?? null,
          maturityMin: res.minMaturity ? dayjs(res.minMaturity) : null,
          maturityMax: res.maxMaturity ? dayjs(res.maxMaturity) : null,
        };
        setBondLadderData(prefilledData);
        setMoodyRatingOptions(res.moodyRatingMap || []);
        setSpRatingOptions(res.spRatingMap || []);
      }
    };
    if (!readOnly) {
      getFilterInitialValues(jwtToken, payload);
    }
  }, [bondLadderData.bondType]);

  const fields = bondTypes[bondLadderData.bondType]?.fields || [];
  // Ensure priority fields appear first (when applicable)
  const priorityOrder = [
    "couponRange",
    "callable",
    "taxable",
    "incomeSource",
    "securityCharge",
    "price",
    "zeroCoupon",
    "state",
    "insured",
  ];
  const orderedFields = [
    ...priorityOrder.filter((f) => fields.includes(f)),
    ...fields.filter((f) => !priorityOrder.includes(f)),
  ];

  const handleChange = (event) => {
    const { name, value } = event.target;
    let isValid = true;
    
    if (name === 'amountPerRung' && value > 10000000) isValid = false;
    if (name === 'numberOfRungs' && value > 10) isValid = false;
    if (name === 'totalMaturity' && value > 600) isValid = false;
    
    if (isValid) {
      const updatedData = { ...bondLadderData, [name]: value };
      
      // Calculate total amount whenever amountPerRung or numberOfRungs changes
      if (name === 'amountPerRung' || name === 'numberOfRungs') {
        const amountPerRung = name === 'amountPerRung' ? value : bondLadderData.amountPerRung || 0;
        const numberOfRungs = name === 'numberOfRungs' ? value : bondLadderData.numberOfRungs || 0;
        
        // Only calculate if both values are present
        if (amountPerRung && numberOfRungs) {
          updatedData.qty = amountPerRung * numberOfRungs;
        }
      }
      
      setBondLadderData(updatedData);

      if (value !== undefined && value !== '') {
        clearFieldError(name);
      }
    }
  };

  // Helper function to determine if a field is editable in edit mode
  const isFieldEditable = (fieldName) => {
    if (readOnly !== "edit") return !readOnly; // Normal behavior for non-edit modes
    
    // In edit mode, only these fields are editable
    const editableFields = [
      'nameOfTheLadder',
      'numberOfRungs', 
      'amountPerRung',
      'totalMaturity'
    ];
    
    return editableFields.includes(fieldName);
  };

  // Helper function to determine if characteristics are editable
  const areCharacteristicsEditable = () => {
    return readOnly !== "edit"; // Characteristics are editable only in edit mode
  };

  const renderAccountField = () => {
    const baseProps = {
      fullWidth: true,
      variant: "filled",
      sx: inputStyle,
      InputProps: { disableUnderline: true }
    };

    const accountProps = {
      ...baseProps,
      error: errors?.account,
    };

    // Always disabled in edit mode or when readOnly is true
    const isDisabled = readOnly === true || readOnly === "edit";

    // Simplified - same for both trader and PM roles
    return (
      <Grid item xs={3}>
        <TextField
          {...accountProps}
          select={!isDisabled}
          name="account"
          value={bondLadderData.account || ""}
          onChange={!isDisabled ? handleChange : undefined}
          label="Account"
          disabled={isDisabled}
        >
          {!isDisabled &&
            effectiveAccountIdentifiers.map((identifier) => (
              <MenuItem key={identifier} value={identifier}>
                {identifier}
              </MenuItem>
            ))}
        </TextField>
      </Grid>
    );
  };

  // Fetch issuers when component mounts
  useEffect(() => {
    const fetchIssuerOptions = async () => {
      try {
        const issuers = await fetchIssuers(jwtToken);
        setIssuerOptions(issuers || []);
      } catch (error) {
        console.error("Error fetching issuers:", error);
        setIssuerOptions([]);
      }
    };

    const fetchRatingOptions = async () => {
      try {
        const [snpRatings, moodyRatings] = await Promise.all([
          fetchSnpRatingOptions(jwtToken),
          fetchMoodyRatingOptions(jwtToken)
        ]);
        setSpRatingOptions(snpRatings || []);
        setMoodyRatingOptions(moodyRatings || []);
      } catch (error) {
        console.error("Error fetching rating options:", error);
        setSpRatingOptions([]);
        setMoodyRatingOptions([]);
      }
    };

    const fetchSecurityCharges = async () => {
      try {
        const charges = await getSecurityChargeOptions(jwtToken);
        setSecurityChargeOptions((charges || []).filter(
          (charge) => charge !== null && charge !== undefined && String(charge).trim() !== ""
        ));
      } catch (error) {
        console.error("Error fetching security charges:", error);
        setSecurityChargeOptions([]);
      }
    };

    fetchIssuerOptions();
    fetchRatingOptions();
    fetchSecurityCharges();
  }, [jwtToken]);

  // Clear validation errors when component mounts - only run once
  useEffect(() => {
    clearValidationErrors();
  }, []); // Remove clearValidationErrors from dependencies

  // Check for validation errors and report to parent - memoize the callback
  const handleValidationChange = useCallback((hasValidationErrors) => {
    if (onValidationChange) {
      onValidationChange(hasValidationErrors);
    }
  }, [onValidationChange]);

  useEffect(() => {
    const hasValidationErrors = Object.values(validationErrors).some(Boolean);
    handleValidationChange(hasValidationErrors);
  }, [validationErrors, handleValidationChange]);

  return (
    <Box sx={{ 
      height: "100%", 
      display: "flex", 
      flexDirection: "column",
      overflow: "hidden",
      position: "relative",
    }}>
      <Box sx={{
        ...stickyBoxStyle,
        flexShrink: 0,
      }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                select
                name="side"
                value={ORDER_SIDES.BUY}
                variant="filled"
                label="Side"
                sx={{
                  ...inputStyle,
                  '& .Mui-disabled': {
                    color: tokens.colors.disabledFontColour,
                    WebkitTextFillColor: tokens.colors.disabledFontColour,
                    backgroundColor: tokens.colors.disabledWhite,
                  },
                  '& .MuiSelect-icon': {
                    display: 'none'
                  }
                  ,
                }}
                InputProps={{ 
                  backgroundColor: tokens.colors.disabledWhite,
                  disableUnderline: true,
                  readOnly: true
                }}
                disabled={true}
              >
                <MenuItem value={ORDER_SIDES.BUY}>
                  {ORDER_SIDES.BUY}
                </MenuItem>
              </TextField>
            </Grid>

            {renderAccountField()}

            <Grid item xs={3}>
              <Box sx={{ position: 'relative', width: '100%' }}>
                <TextField
                  fullWidth
                  name="nameOfTheLadder"
                  value={bondLadderData.nameOfTheLadder || ""}
                  onChange={isFieldEditable('nameOfTheLadder') ? (e) => {
                    if (e.target.value.length <= 40) {
                      handleChange(e);
                    }
                  } : undefined}
                  sx={inputStyle}
                  placeholder="Enter ladder name"
                  variant="filled"
                  label="Name of the ladder"
                  InputProps={{ disableUnderline: true }}
                  disabled={!isFieldEditable('nameOfTheLadder')}
                  error={errors?.nameOfTheLadder}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 8,
                    color: (bondLadderData.nameOfTheLadder?.length || 0) > 40 ? 'error.main' : 'text.secondary',
                    fontSize: '0.50rem',
                    pointerEvents: 'none'
                  }}
                >
                  {bondLadderData.nameOfTheLadder?.length || 0}/40
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <IntegerInput
                name="numberOfRungs"
                value={bondLadderData.numberOfRungs || ""}
                onChange={isFieldEditable('numberOfRungs') ? handleChange : () => {}}
                placeholder="Enter number of rungs"
                label="Number of rungs"
                disabled={!isFieldEditable('numberOfRungs')}
                error={errors?.numberOfRungs}
                maxValue={10}
                sx={inputStyle}
              />
            </Grid>

            <Grid item xs={3}>
              <IntegerInput
                name="amountPerRung"
                value={bondLadderData.amountPerRung || ""}
                onChange={isFieldEditable('amountPerRung') ? handleChange : () => {}}
                placeholder="Enter amount per rung"
                label="Amount per rung"
                disabled={!isFieldEditable('amountPerRung')}
                error={errors?.amountPerRung}
                maxValue={10000000}
                sx={inputStyle}
              />
            </Grid>
            
            <Grid item xs={3}>
              <IntegerInput
                name="qty"
                value={bondLadderData.qty || ""}
                onChange={() => {}}
                label="Total amount"
                disabled={true}
                error={errors?.qty}
                sx={{
                  ...inputStyle,
                  '& .Mui-disabled': {
                    color: tokens.colors.disabledFontColour,
                    WebkitTextFillColor: tokens.colors.disabledFontColour,
                  }
                }}
              />
            </Grid>

            <Grid item xs={3}>
              <IntegerInput
                name="totalMaturity"
                value={bondLadderData.totalMaturity || ""}
                onChange={isFieldEditable('totalMaturity') ? handleChange : () => {}}
                placeholder="Enter total maturity in months"
                label="Total Maturity (Months)"
                disabled={!isFieldEditable('totalMaturity')}
                error={errors?.totalMaturity}
                maxValue={600}
                sx={inputStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1, mb: 2, alignItems: "center" }}>
            <Grid item xs={3} sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body1" sx={{ mr: 1 }}>
                Type
              </Typography>
              <Select
                fullWidth
                name="bondType"
                value={bondLadderData.bondType || ""}
                onChange={readOnly === true ? undefined : handleChange}
                displayEmpty
                disabled={readOnly === true}
                sx={{ height: "40px", ...inputStyle }}
              >
                {bondLadderTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={9}>
              <Box sx={{ position: "relative", width: "100%" }}>
                <TextareaAutosize
                  name="comments"
                  value={bondLadderData.comments || ""}
                  onChange={(e) => {
                    if (e.target.value.length <= 200) {
                      handleChange(e);
                    }
                  }}
                  onFocus={() => setIsCommentsFocused(true)}
                  onBlur={() => setIsCommentsFocused(false)}
                  placeholder="Add Comments"
                  disabled={readOnly === true}
                  minRows={1}
                  maxRows={5}
                  style={{
                    ...textAreaStyle,
                    outline: 'none', // remove default browser outline
                    border: `1px solid ${isCommentsFocused ? tokens.palette.primary : tokens.colors.bodyBorderColor50}`,
                    boxShadow: isCommentsFocused ? `0 0 0 3px ${tokens.palette.primaryA10 || tokens.palette.primaryA30}` : 'none',
                    WebkitBoxShadow: isCommentsFocused ? `0 0 0 3px ${tokens.palette.primaryA10 || tokens.palette.primaryA30}` : 'none',
                    ...(readOnly === true && {
                      opacity: 0.95,
                      backgroundColor: tokens.colors.defaultGridRowColor,
                      cursor: 'not-allowed',
                      color: tokens.colors.disabledFontColour, // use disabled token
                      border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                      boxShadow: 'none',
                      WebkitBoxShadow: 'none',
                    })
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 8,
                    color: (bondLadderData.comments?.length || 0) > 200 ? 'error.main' : tokens.colors.disabledFontColour,
                    fontSize: '0.75rem',
                    pointerEvents: 'none'
                  }}
                >
                  {bondLadderData.comments?.length || 0}/200
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box sx={{
        flex: 1,
        overflow: "auto",
        minHeight: 0,
        height: "100%",
      }}>
        {viewAsChips ? (
          <Box sx={{ p: 2, pt: 1 }}>
            <FilterDetailsBoxes input={chipInput} />
          </Box>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
            p: 2,
            pt: 1,
            minHeight: "min-content",
          }}>
            {orderedFields
              .filter((field) => field !== "maturityRanges")
              .map((field) => (
                 <BondCharacteristicField
                   key={field}
                   panelName="bondLadder"
                   checked={checked}
                   onCheckboxChange={handleCheckboxChange}
                   subtab={bondLadderData.bondType}
                   field={field}
                   readOnly={readOnly === true ? true : false} // Characteristics are editable in edit mode
                   bondCharacteristicData={bondLadderData}
                   setBondCharacteristicData={setBondLadderData}
                   handleBondCharacteristicChange={handleChange}
                   fieldOptions={fieldOptions}
                   inputStyle={inputStyle}
                   issuerOptions={issuerOptions}
                   moodyRatingOptions={moodyRatingOptions}
                   spRatingOptions={spRatingOptions}
                   securityChargeOptions={securityChargeOptions}
                   onValidationChange={onValidationChange}
                 />
               ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BondLadderTab;
